import { Device, DeviceCategory } from '../../installation/forms'
import { BatteryTrainingMode } from '../../installation/api'
import { isNil } from 'lodash'

export type DeviceError = {
  /** The error will still be counted but its details will only be shown to super users */
  adminOnly: boolean
  type: 'Error' | 'Warning'
  /** translation key */
  key: string
}

type DeviceErrorsDetails = {
  numberOfErrors: number
  numberOfWarnings: number
  errorsDetails: DeviceError[]
}

type ErrorBitMapping = {
  /**
   * Zero-based index of the bit to check. (Bits read from right-to-left).
   * Example: in 1001, bit 1 is 10[0]1
   */
  bit: number
} & DeviceError

/** Error configurations */

const meterErrors: ErrorBitMapping[] = [
  {
    adminOnly: true,
    bit: 20,
    type: 'Error',
    key: 'installation.deviceStatus.meter-error.in-service',
  },
  {
    adminOnly: true,
    bit: 19,
    type: 'Error',
    key: 'installation.deviceStatus.meter-error.not-connected',
  },
  {
    adminOnly: true,
    bit: 18,
    type: 'Error',
    key: 'installation.deviceStatus.meter-error.not-alive',
  },
  {
    adminOnly: true,
    bit: 17,
    type: 'Error',
    key: 'installation.deviceStatus.meter-error.not-working',
  },
  {
    adminOnly: false,
    bit: 2,
    type: 'Warning',
    key: 'installation.deviceStatus.meter-error.wrong-wiring-config',
  },
  {
    adminOnly: true,
    bit: 1,
    type: 'Warning',
    key: 'installation.deviceStatus.meter-error.silenced',
  },
]

const wallboxErrors: ErrorBitMapping[] = [
  {
    adminOnly: true,
    bit: 20,
    type: 'Error',
    key: 'installation.deviceStatus.evse-error.in-service',
  },
  {
    adminOnly: true,
    bit: 19,
    type: 'Error',
    key: 'installation.deviceStatus.evse-error.not-connected',
  },
  {
    adminOnly: true,
    bit: 18,
    type: 'Error',
    key: 'installation.deviceStatus.evse-error.not-alive',
  },
  {
    adminOnly: true,
    bit: 17,
    type: 'Error',
    key: 'installation.deviceStatus.evse-error.not-working',
  },
  {
    adminOnly: false,
    bit: 2,
    type: 'Warning',
    key: 'installation.summaryTable.warning.ModbusWallboxMismatch',
  },
  {
    adminOnly: true,
    bit: 3,
    type: 'Warning',
    key: 'installation.summaryTable.warning.EVCS.OcppConnectionError',
  },
  {
    adminOnly: true,
    bit: 1,
    type: 'Warning',
    key: 'installation.deviceStatus.evse-error.silenced',
  },
]

const storageErrors: ErrorBitMapping[] = [
  {
    adminOnly: true,
    bit: 25,
    type: 'Error',
    key: 'installation.deviceStatus.storage-error.pvi-not-working',
  },
  {
    adminOnly: true,
    bit: 24,
    type: 'Error',
    key: 'installation.deviceStatus.storage-error.dc-in-service',
  },
  {
    adminOnly: true,
    bit: 23,
    type: 'Error',
    key: 'installation.deviceStatus.storage-error.dc-not-connected',
  },
  {
    adminOnly: true,
    bit: 21,
    type: 'Error',
    key: 'installation.deviceStatus.storage-error.dc-not-working',
  },
  {
    adminOnly: true,
    bit: 19,
    type: 'Error',
    key: 'installation.deviceStatus.storage-error.battery-not-connected',
  },
  {
    adminOnly: true,
    bit: 18,
    type: 'Error',
    key: 'installation.deviceStatus.storage-error.not-alive',
  },
  {
    adminOnly: true,
    bit: 17,
    type: 'Error',
    key: 'installation.deviceStatus.storage-error.battery-not-working',
  },
  {
    adminOnly: true,
    bit: 3,
    type: 'Warning',
    key: 'installation.deviceStatus.storage-error.battery-voltage-adjustment-active',
  },
  {
    adminOnly: false,
    bit: 2,
    type: 'Warning',
    key: 'installation.deviceStatus.storage-error.battery-training',
  },
]

const sgReadyErrors: ErrorBitMapping[] = [
  {
    adminOnly: true,
    bit: 18,
    type: 'Error',
    key: 'installation.deviceStatus.sg-ready-error.not-ready-to-use',
  },
  {
    adminOnly: true,
    bit: 17,
    type: 'Error',
    key: 'installation.deviceStatus.sg-ready-error.disabled',
  },
]

const ioControllerErrors: ErrorBitMapping[] = [
  {
    adminOnly: true,
    bit: 20,
    type: 'Error',
    key: 'installation.deviceStatus.io-controller-error.in-service',
  },
  {
    adminOnly: true,
    bit: 19,
    type: 'Error',
    key: 'installation.deviceStatus.io-controller-error.not-connected',
  },
  {
    adminOnly: true,
    bit: 18,
    type: 'Error',
    key: 'installation.deviceStatus.io-controller-error.not-alive',
  },
  {
    adminOnly: true,
    bit: 17,
    type: 'Error',
    key: 'installation.deviceStatus.io-controller-error.not-working',
  },
]

const emcErrors: ErrorBitMapping[] = [
  { adminOnly: true, bit: 18, type: 'Error', key: 'installation.deviceStatus.emc-error.not-alive' },
  {
    adminOnly: true,
    bit: 17,
    type: 'Error',
    key: 'installation.deviceStatus.emc-error.not-working',
  },
]

/**
 * Returns the errors and warnings of a device based on its status code, as well as the number of errors and warnings.
 * If the user in not a super user, the number of errors and warnings will be returned but the errors array will
 * only contain user appropiate error/warning messages.
 * If there is no device or no status code, it returns null.
 */
export const getDeviceErrorsDetails = (
  device: Device,
  batteryTrainigMode: BatteryTrainingMode,
  showDetailedErrors: boolean
): DeviceErrorsDetails | null => {
  if (!device) return null

  const { deviceStatusCodeBits, deviceCategory } = device
  if (isNil(deviceStatusCodeBits)) return null

  /**
   * Handle edge case for battery training mode.
   * Status code is 0 even is battery is in training mode but a warning
   * should still be displayed.
   */
  const isInBatteryTrainingMode = deviceCategory === 'Storage' && batteryTrainigMode === 2

  const batteryTrainingWarning: DeviceError[] = isInBatteryTrainingMode
    ? [
        {
          adminOnly: false,
          type: 'Warning',
          key: 'installation.deviceStatus.storage-error.battery-training',
        },
      ]
    : []

  if (deviceStatusCodeBits === 0)
    return {
      numberOfErrors: 0,
      numberOfWarnings: isInBatteryTrainingMode ? 1 : 0,
      errorsDetails: batteryTrainingWarning,
    }

  const statusCodeBinary = integerToBinary(deviceStatusCodeBits)

  const errors = getDeviceSpecificErrors(deviceCategory, statusCodeBinary)
  const numberOfErrors = errors.filter(error => error.type === 'Error').length
  const numberOfWarnings = errors.filter(error => error.type === 'Warning').length + (isInBatteryTrainingMode ? 1 : 0)

  const filteredErrors = showDetailedErrors ? errors : errors.filter(error => !error.adminOnly)

  return {
    numberOfErrors,
    numberOfWarnings,
    errorsDetails: [...batteryTrainingWarning, ...filteredErrors],
  }
}

const deviceTypeToErrorsMap: Record<DeviceCategory, ErrorBitMapping[]> = {
  EMC: emcErrors,
  Storage: storageErrors,
  'ModbusTCP Evse': wallboxErrors,
  'EEBus Evse': wallboxErrors,
  Heatpump: ioControllerErrors,
  Meter: meterErrors,
  'HTC320H I/O': sgReadyErrors,
  'Storage I/O': sgReadyErrors,
  'MyPV I/O': ioControllerErrors,
}
/** Returns the erros and warnings of a device based on its type. */
const getDeviceSpecificErrors = (category: DeviceCategory, statusCodeBinary: string): DeviceError[] => {
  return getDeviceErrorsFromBits(statusCodeBinary, deviceTypeToErrorsMap[category])
}

/**
 * Gets a list of errors where their corresponding bit is true.
 * @param statusCodeBinary A string that is a binary representation of a number
 * @param errors A list of possible errors based on the bit of the binary
 * @returns A list of all the errors that apply for this binary
 */
const getDeviceErrorsFromBits = (statusCodeBinary: string, errors: ErrorBitMapping[]): DeviceError[] => {
  return errors.flatMap(({ bit, type, key, adminOnly }) => {
    const hasError = checkIfBitTrue(statusCodeBinary, bit)
    if (hasError) return [{ type, key, adminOnly }]
    else return []
  })
}

/**
 * Turns a number to its binary representation (as a string).
 *
 * To avoid an edge case with negative numbers, it bit-shifts to the right by 0. That does not change the number itself,
 * but it turns the representation to an unsigned integer.
 * - Without bit shift: `(-1).toString(2)` -> `"-1"`
 * - With bit shift: `(-1 >>> 0).toString(2)` -> `"11111111111111111111111111111111"`
 */
const integerToBinary = (integer: number): string => {
  return (integer >>> 0).toString(2)
}

/**
 * Returns true if the nth bit of the binary is '1'
 * @param binary A string that is a binary representation of a number
 * @param bit Zero indexed position of the bit to be checked.
 * Bit position is indexed from the end of the string.
 */
const checkIfBitTrue = (binary: string, bit: number): boolean => {
  return binary.charAt(binary.length - 1 - bit) === '1'
}
